import { defineStore } from 'pinia';
import { ContactUsOnlineService as _contactUsOnline } from '@/services/store/contact-us-online.service';
import { computed, ref } from 'vue';
import { IContactUsContent, ContactUsModel, LabeledValue } from '@/interfaces/pages/contact-us-form.interface';
import { useAccountStore, useFormStepDisclaimerStore, useFormStore } from '@/store';
import { IDisclaimer, IFormStep } from '@/interfaces';

export const useContactUsOnlineStore = defineStore('ContactUsOnline', () => {
  // ------------------------------------------------------------- //
  // Define Store
  // ------------------------------------------------------------- //
  const accountStore = useAccountStore();
  const formStepDisclaimerStore = useFormStepDisclaimerStore();
  const formStore = useFormStore();

  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const isMemberLoggedIn = ref<boolean>(false);
  const content = ref<IContactUsContent>();
  const model = ref<ContactUsModel>({
    yourComments: '',
  });
  const showEntraLogin = ref<boolean>(false);

  // ------------------------------------------------------------- //
  // Define Computed
  // ------------------------------------------------------------- //
  const selectedEnquiryType = computed(() => {
    return content
      .value!.enquiryCategory.find((x) => x.enquiryId == model.value.enquiryCategory)
      ?.enquiryTypes.find((y) => y.enquiryId == model.value.enquiryType);
  });

  const selectedEnquiryCategory = computed(() => {
    return content.value!.enquiryCategory.find((x) => x.enquiryId == model.value.enquiryCategory);
  });

  const selectedEnquiryTopic = computed(() => {
    if (!model.value.enquiryTopic) {
      return null;
    }
    return content
      .value!.enquiryCategory.find((x) => x.enquiryId == model.value.enquiryCategory)
      ?.enquiryTypes.find((y) => y.enquiryId == model.value.enquiryType)
      ?.enquiryTopic.find((x) => x.enquiryId == model.value.enquiryTopic);
  });

  // ------------------------------------------------------------- //
  // Define Functions
  // ------------------------------------------------------------- //
  const getContactUsContent = async () => {
    content.value = await _contactUsOnline.getContactUsContent();
  };
  /**
   * Send contact us email
   * @returns
   */
  const sendEmail = async (): Promise<boolean> => {
    return await _contactUsOnline.sendContactUsEmail(model.value);
  };

  /**
   * Map personal details from form fields that are either: filled from entra model or manually input
   * @param values
   */
  const mapPersonalDetails = (values?: Record<string, any>) => {
    model.value.firstName = values?.firstName ?? values?.firstNameOptional;
    model.value.lastName = values?.lastName ?? values?.lastNameOptional;
    model.value.emailAddress = values?.emailAddress;
    model.value.phoneNumber = values?.phoneNumber ?? values?.phoneNumberOptional;
    model.value.membershipCardNumber = values?.membershipCardNumber;
  };

  /**
   * Map enquiry details
   * @param fields
   */
  const mapEquiryDetails = async (fields?: LabeledValue[]) => {
    model.value.selectedEnquiryCategory = selectedEnquiryCategory.value;
    model.value.selectedEnquiryTopic = selectedEnquiryTopic.value;
    model.value.selectedEnquiryType = selectedEnquiryType.value;
    model.value.fields = fields;
    model.value.verifiedThroughSelfService = await accountStore.isAuthenticated;
  };

  /**
   * map enquiry category details
   * @param values
   */
  const mapEnquiryCategory = async (values?: Record<string, any>) => {
    model.value.enquiryCategory = values?.enquiryCategory;
    model.value.enquiryType = values?.enquiryType;
    model.value.enquiryTopic = values?.enquiryTopic;

    const isAuthenticated = await accountStore.isAuthenticated;
    if (isAuthenticated) {
      model.value.firstName = accountStore.identityDetails?.firstName;
      model.value.lastName = accountStore.identityDetails?.lastName;
      model.value.emailAddress = accountStore.identityDetails?.email;
      model.value.phoneNumber = accountStore.identityDetails?.phoneNumber;
      model.value.membershipCardNumber = accountStore.identityDetails?.membershipCardNumber;
      showEntraLogin.value = false;
    } else {
      showEntraLogin.value =
        content.value?.enquiryCategory
          .find((x) => x.enquiryId == model.value?.enquiryCategory)
          ?.enquiryTypes.find((y) => y.enquiryId == model.value?.enquiryType)?.requireEntraLogin ?? false;
    }
  };

  const mapPersonOrOrg = async (fields?: LabeledValue[]) => {
    model.value.selectedEnquiryCategory = selectedEnquiryCategory.value;
    model.value.selectedEnquiryTopic = selectedEnquiryTopic.value;
    model.value.selectedEnquiryType = selectedEnquiryType.value;
    model.value.fields = fields;
    model.value.verifiedThroughSelfService = await accountStore.isAuthenticated;
  };

  const mapFraudDetails = async (fields?: LabeledValue[]) => {
    model.value.fields2 = fields;
  };

  const clearComments = () => {
    model.value.yourComments = '';
  };

  const setComments = (comment: string) => {
    model.value.yourComments = comment;
  };

  const setDisclaimers = (selectedEnquiryCategory: string) => {
    const additionalDisclaimer = [] as IDisclaimer[];
    formStore.inScopeSteps.forEach((step: IFormStep) => {
      const enquiryCategory = content.value!.enquiryCategory.find((x) => x.enquiryId == selectedEnquiryCategory);
      additionalDisclaimer.push({
        stepTitle: step.stepTitle ?? '',
        disclaimers: [enquiryCategory?.disclaimer ?? ''],
      });
    });
    formStepDisclaimerStore.additionalDisclaimer = additionalDisclaimer;
  };

  function $reset() {}

  return {
    isMemberLoggedIn,
    model,
    getContactUsContent,
    content,
    mapPersonalDetails,
    mapEnquiryCategory,
    selectedEnquiryType,
    clearComments,
    setComments,
    selectedEnquiryCategory,
    selectedEnquiryTopic,
    sendEmail,
    mapEquiryDetails,
    setDisclaimers,
    mapPersonOrOrg,
    mapFraudDetails,
    showEntraLogin,
  };
});
